// Sugalarm — Cycle 351: the FIRE-TIME predicate for the cycle-232
// sustained high-side chime cadence.
//
// WHY THIS EXISTS (operator incident, 2026-07-19 05:53 CDT / 10:53 UTC):
// the app was opened on an iPhone and sounded the member's real high alarm
// tone while their live reading was 110 and in range. The live DB shows no
// alert row, no prediction, and no notification_delivery of any rail for
// that member that day — no server-driven path could have produced it.
//
// The cycle-232 cadence arms a 60-minute `setInterval` while a member sits
// high-side, and the callback re-checked ONLY `alerts_enabled`. It never
// re-evaluated the conditions it was armed on. iOS WKWebView / Android
// WebView SUSPEND JS timers while backgrounded and fire the overdue
// callback on foreground (the same webview behavior cycle 309 documented
// for the auto-announce cadence) — so an interval armed during a real high
// could fire its chime at app-open long after the member had recovered,
// against a reading that is now fresh and in range.
//
// This is the SAME lesson the server learned on the repeating rails in
// cycles 346 and 349: a repeating alarm rail must re-evaluate the member's
// CURRENT state at SEND time, never trust the state captured when it armed
// (see `_sweep_voice_blitz` / `_sweep_contact_sms`). The value an alarm
// asserts must be the member's situation RIGHT NOW.
//
// Deliberately scoped to this one rail. The cross-surface "single
// emit-gate" consolidation is a separate, named follow-up.

import type { Zone } from "./types";

/** The platform `Zone` (which includes `"unknown"`), or null when there is
 *  no reading at all. Both non-high values are silent by construction. */
export type CueZone = Zone | null;

export interface HighCadenceCueState {
  /** The member's `alerts_enabled` setting. */
  enabled: boolean;
  /** The most recent reading is inside the provider-aware staleness
   *  window. A dead sensor never sounds (stale → neutral). */
  readingFresh: boolean;
  /** Zone of the CURRENT reading, evaluated against current thresholds. */
  zone: CueZone;
  /** The episode is live and unmuted (`phase === "active" && !silenced`;
   *  false while snoozed, stopped, or recovered). Null = no episode. */
  episodeAudioOn: boolean | null;
}

/** True only when a sustained high-side chime is legitimate RIGHT NOW.
 *
 *  Evaluated at BOTH arm time and fire time. A real, still-live high
 *  satisfies it at fire time exactly as it did at arm time, so a genuine
 *  high is never delayed, weakened, or muted — the predicate only refuses
 *  a chime whose justification has already ended. */
export function mayEmitHighCadenceCue(s: HighCadenceCueState): boolean {
  return (
    s.enabled &&
    s.readingFresh &&
    (s.zone === "high" || s.zone === "urgent_high") &&
    s.episodeAudioOn === true
  );
}

// ─── Cycle 429: the ONE age guard every event cue reads ───────────────
//
// The high chime had a staleness guard since cycle 112 (ten minutes on the
// row's `fired_at`); no other cue had one. On 2026-09-10 a socket that
// survived an iOS freeze delivered its whole backlog in one burst on
// foreground, and every buffered event fired its cue — thirteen crossings
// and five recoveries in a few hundred milliseconds. Now every EVENT cue
// (a crossing chime, the recovery cue, the return-to-range cue, the
// prediction sound) plays only for an event whose own timestamp is within
// `CUE_EVENT_AGE_MS` of now. STATE cues — the low loop, the kiosk's
// repeating urgent cue — are deliberately NOT gated here: they reflect
// what is sounding now, and a device that reconnects mid-episode must
// still sound (cycle 231). Thirty seconds is far past the fan-out's ~2 s
// and far short of the next reading.

export const CUE_EVENT_AGE_MS = 30_000;

/** Is an event fresh enough to be cued? A missing or unparsable stamp is
 *  NOT fresh — silence is the safe direction for a one-shot cue whose
 *  event we cannot date (the alarm's persistence lives in the loops, the
 *  pushes and the calls, never in a one-shot). */
export function cueEventFresh(
  stamp: string | number | null | undefined,
  nowMs: number,
  boundMs: number = CUE_EVENT_AGE_MS,
): boolean {
  if (stamp == null) return false;
  const ms = typeof stamp === "number" ? stamp : new Date(stamp).getTime();
  if (!Number.isFinite(ms)) return false;
  return nowMs - ms <= boundMs;
}
